import React, { useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Badge,
  InputGroup,
  FormControl,
  ProgressBar,
  Tab,
  Nav,
  Modal,
} from "react-bootstrap";
import {
  FaCopy,
  FaCheck,
  FaUpload,
  FaTimes,
  FaImage,
  FaVideo,
  FaTrash,
  FaLink,
  FaRegCopy,
  FaSpinner,
  FaFolder,
  FaDownload,
  FaEye,
  FaCloudUploadAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Baseurl, Imageurl } from "../baseurl";


const UploadMedia = () => {
  // State management
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [copiedPath, setCopiedPath] = useState(null);
  const [activeTab, setActiveTab] = useState("images");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Refs for file inputs
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // File validation
  const validateFile = (file, type) => {
    const maxImageSize = 5 * 1024 * 1024; // 5MB
    const maxVideoSize = 100 * 1024 * 1024; // 100MB
    const allowedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    const allowedVideoTypes = ["video/mp4", "video/webm", "video/ogg"];

    if (type === "image") {
      if (!allowedImageTypes.includes(file.type)) {
        toast.error(
          "Invalid image format. Please use JPEG, PNG, WebP, or GIF.",
        );
        return false;
      }
      if (file.size > maxImageSize) {
        toast.error("Image size exceeds 5MB limit.");
        return false;
      }
    } else if (type === "video") {
      if (!allowedVideoTypes.includes(file.type)) {
        toast.error("Invalid video format. Please use MP4, WebM, or OGG.");
        return false;
      }
      if (file.size > maxVideoSize) {
        toast.error("Video size exceeds 100MB limit.");
        return false;
      }
    }

    return true;
  };

  // Your upload function
  const uploadFilesToServer = async (files, type) => {
    try {
      const formDataToSend = new FormData();
      files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress((prev) => ({
            ...prev,
            [files[0]?.name || "upload"]: percentCompleted,
          }));
        },
      };

      const endpoint = type === "image" ? "images" : "videos";
      const res = await axios.post(
        `${Imageurl}/auctionVehicleUpload`,
        formDataToSend,
        config,
      );

      if (res.data && res.data.success) {
        return {
          success: true,
          filePaths: res.data.filePaths,
        };
      }
      return { success: false, error: "Upload failed" };
    } catch (error) {
      console.error(`${type} upload error:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => validateFile(file, "image"));

    if (validFiles.length === 0) return;

    setIsUploading(true);

    // Create preview objects
    const newImageFiles = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: "image",
      status: "pending",
      path: null,
    }));

    setImageFiles((prev) => [...prev, ...newImageFiles]);

    // Upload to server
    const uploadResult = await uploadFilesToServer(validFiles, "image");

    if (uploadResult.success) {
      // Update files with paths
      const updatedFiles = newImageFiles.map((item, index) => ({
        ...item,
        status: "uploaded",
        path: uploadResult.filePaths[index],
      }));

      // Move to uploaded media
      setUploadedMedia((prev) => [...prev, ...updatedFiles]);

      // Remove from pending images
      setImageFiles((prev) =>
        prev.filter((f) => !validFiles.some((vf) => vf.name === f.name)),
      );

      toast.success(`${validFiles.length} image(s) uploaded successfully!`);
    } else {
      toast.error(`Failed to upload images: ${uploadResult.error}`);
      // Remove failed files
      setImageFiles((prev) =>
        prev.filter((f) => !validFiles.some((vf) => vf.name === f.name)),
      );
    }

    setIsUploading(false);
    e.target.value = ""; // Reset input
  };

  // Handle video upload
  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => validateFile(file, "video"));

    if (validFiles.length === 0) return;

    setIsUploading(true);

    // Create preview objects
    const newVideoFiles = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: "video",
      status: "pending",
      path: null,
    }));

    setVideoFiles((prev) => [...prev, ...newVideoFiles]);

    // Upload to server
    const uploadResult = await uploadFilesToServer(validFiles, "video");

    if (uploadResult.success) {
      // Update files with paths
      const updatedFiles = newVideoFiles.map((item, index) => ({
        ...item,
        status: "uploaded",
        path: uploadResult.filePaths[index],
      }));

      // Move to uploaded media
      setUploadedMedia((prev) => [...prev, ...updatedFiles]);

      // Remove from pending videos
      setVideoFiles((prev) =>
        prev.filter((f) => !validFiles.some((vf) => vf.name === f.name)),
      );

      toast.success(`${validFiles.length} video(s) uploaded successfully!`);
    } else {
      toast.error(`Failed to upload videos: ${uploadResult.error}`);
      // Remove failed files
      setVideoFiles((prev) =>
        prev.filter((f) => !validFiles.some((vf) => vf.name === f.name)),
      );
    }

    setIsUploading(false);
    e.target.value = ""; // Reset input
  };

  // Handle file removal
  const handleRemoveFile = (index, type) => {
    if (type === "image") {
      setImageFiles((prev) => prev.filter((_, i) => i !== index));
    } else if (type === "video") {
      setVideoFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Handle uploaded media removal
  const handleRemoveUploadedMedia = (index) => {
    setUploadedMedia((prev) => prev.filter((_, i) => i !== index));
    toast.info("Media removed from list");
  };

  // Copy path to clipboard
  const copyToClipboard = (path) => {
    navigator.clipboard
      .writeText(path)
      .then(() => {
        setCopiedPath(path);
        toast.success("Path copied to clipboard!");
        setTimeout(() => setCopiedPath(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy path");
      });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Open preview modal
  const openPreview = (item) => {
    setPreviewItem(item);
    setShowPreviewModal(true);
  };

  // Get full URL for display
  const getFullUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${Imageurl}/${path.replace(/^\//, "")}`;
  };

  // Render pending files section
  const renderPendingFiles = (files, type) => {
    if (files.length === 0) return null;

    return (
      <div className="mb-4">
        <h6 className="mb-3">
          {type === "image" ? (
            <FaImage className="me-2" />
          ) : (
            <FaVideo className="me-2" />
          )}
          Pending {type === "image" ? "Images" : "Videos"} ({files.length})
        </h6>
        <Row>
          {files.map((item, index) => (
            <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-3">
              <Card className="h-100">
                <div
                  className="position-relative"
                  style={{ height: "150px", cursor: "pointer" }}
                  onClick={() => openPreview(item)}
                >
                  {item.type === "image" ? (
                    <img
                      src={item.preview}
                      alt={item.name}
                      className="card-img-top h-100"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <video
                      src={item.preview}
                      className="card-img-top h-100"
                      style={{ objectFit: "cover" }}
                    />
                  )}
                  <Badge
                    bg="warning"
                    className="position-absolute top-0 end-0 m-2"
                  >
                    Pending
                  </Badge>
                </div>
                <Card.Body className="p-2">
                  <small className="text-truncate d-block" title={item.name}>
                    {item.name}
                  </small>
                  <small className="text-muted d-block">
                    {formatFileSize(item.size)}
                  </small>
                  {uploadProgress[item.name] && (
                    <ProgressBar
                      now={uploadProgress[item.name]}
                      label={`${uploadProgress[item.name]}%`}
                      className="mt-2"
                      variant="info"
                    />
                  )}
                </Card.Body>
                <Card.Footer className="p-2">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100"
                    onClick={() => handleRemoveFile(index, type)}
                  >
                    <FaTrash className="me-1" /> Remove
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render uploaded media section
  const renderUploadedMedia = () => {
    if (uploadedMedia.length === 0) return null;

    const uploadedImages = uploadedMedia.filter(
      (item) => item.type === "image",
    );
    const uploadedVideos = uploadedMedia.filter(
      (item) => item.type === "video",
    );

    return (
      <div className="mt-5">
        <h4 className="mb-4">Uploaded Media</h4>

        {uploadedImages.length > 0 && (
          <div className="mb-5">
            <h5 className="mb-3">
              <FaImage className="me-2" /> Images ({uploadedImages.length})
            </h5>
            <Row>
              {uploadedImages.map((item, index) => (
                <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-3">
                  <MediaCard item={item} index={index} />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {uploadedVideos.length > 0 && (
          <div>
            <h5 className="mb-3">
              <FaVideo className="me-2" /> Videos ({uploadedVideos.length})
            </h5>
            <Row>
              {uploadedVideos.map((item, index) => (
                <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-3">
                  <MediaCard item={item} index={index} />
                </Col>
              ))}
            </Row>
          </div>
        )}
      </div>
    );
  };

  // Media Card Component
  const MediaCard = ({ item, index }) => (
    <Card className="h-100 shadow-sm">
      <div
        className="position-relative"
        style={{ height: "150px", cursor: "pointer" }}
        onClick={() => openPreview(item)}
      >
        {item.type === "image" ? (
          <img
            src={item.path ? getFullUrl(item.path) : item.preview}
            alt={item.name}
            className="card-img-top h-100"
            style={{ objectFit: "cover" }}
          />
        ) : (
          <video
            src={item.path ? getFullUrl(item.path) : item.preview}
            className="card-img-top h-100"
            style={{ objectFit: "cover" }}
          />
        )}
        <Badge bg="success" className="position-absolute top-0 end-0 m-2">
          <FaCheck className="me-1" /> Uploaded
        </Badge>
        <div className="position-absolute bottom-0 start-0 m-2">
          <Badge bg="dark">{item.type}</Badge>
        </div>
      </div>
      <Card.Body className="p-3">
        <h6 className="text-truncate" title={item.name}>
          {item.name}
        </h6>
        <p className="small text-muted mb-2">{formatFileSize(item.size)}</p>

        {item.path && (
          <div className="mb-3">
            <small className="text-muted d-block mb-1">File Path:</small>
            <InputGroup size="sm">
              <FormControl value={item.path} readOnly className="bg-light" />
              <Button
                variant={
                  copiedPath === item.path ? "success" : "outline-secondary"
                }
                onClick={() => copyToClipboard(item.path)}
              >
                {copiedPath === item.path ? <FaCheck /> : <FaRegCopy />}
              </Button>
            </InputGroup>
            <small className="text-muted d-block mt-1">
              Full URL: {getFullUrl(item.path)}
            </small>
          </div>
        )}

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => openPreview(item)}
          >
            <FaEye className="me-1" /> Preview
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleRemoveUploadedMedia(index)}
          >
            <FaTrash className="me-1" /> Remove
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">
            <FaCloudUploadAlt className="me-3" />
            Media Upload Manager
          </h2>
          <p className="text-muted">Upload and manage your images and videos</p>
        </Col>
      </Row>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Row>
          <Col md={3}>
            <Card className="sticky-top" style={{ top: "20px" }}>
              <Card.Body>
                <Nav variant="pills" className="flex-column">
                  <Nav.Item>
                    <Nav.Link eventKey="images" className="mb-2">
                      <FaImage className="me-2" />
                      Upload Images
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="videos" className="mb-2">
                      <FaVideo className="me-2" />
                      Upload Videos
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="uploaded">
                      <FaFolder className="me-2" />
                      Uploaded Media ({uploadedMedia.length})
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <hr />

                <div className="mt-3">
                  <h6 className="text-muted mb-3">Quick Stats</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Pending Images:</span>
                    <Badge bg="warning">{imageFiles.length}</Badge>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Pending Videos:</span>
                    <Badge bg="warning">{videoFiles.length}</Badge>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Uploaded Images:</span>
                    <Badge bg="success">
                      {uploadedMedia.filter((m) => m.type === "image").length}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Uploaded Videos:</span>
                    <Badge bg="success">
                      {uploadedMedia.filter((m) => m.type === "video").length}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={9}>
            <Tab.Content>
              {/* Images Tab */}
              <Tab.Pane eventKey="images">
                <Card>
                  <Card.Body>
                    <h4 className="mb-4">
                      <FaImage className="me-3" />
                      Upload Images
                    </h4>

                    <div className="text-center p-5 border rounded bg-light mb-4">
                      <FaUpload size={50} className="text-muted mb-3" />
                      <h5>Drag & Drop Images Here</h5>
                      <p className="text-muted mb-3">
                        or click to browse. Max 5MB per image (JPEG, PNG, WebP,
                        GIF)
                      </p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => imageInputRef.current.click()}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <FaSpinner className="me-2 spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <FaUpload className="me-2" />
                            Select Images
                          </>
                        )}
                      </Button>
                      <Form.Control
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="d-none"
                      />
                    </div>

                    {renderPendingFiles(imageFiles, "image")}
                    {renderUploadedMedia()}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Videos Tab */}
              <Tab.Pane eventKey="videos">
                <Card>
                  <Card.Body>
                    <h4 className="mb-4">
                      <FaVideo className="me-3" />
                      Upload Videos
                    </h4>

                    <div className="text-center p-5 border rounded bg-light mb-4">
                      <FaUpload size={50} className="text-muted mb-3" />
                      <h5>Drag & Drop Videos Here</h5>
                      <p className="text-muted mb-3">
                        or click to browse. Max 100MB per video (MP4, WebM, OGG)
                      </p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => videoInputRef.current.click()}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <FaSpinner className="me-2 spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <FaUpload className="me-2" />
                            Select Videos
                          </>
                        )}
                      </Button>
                      <Form.Control
                        type="file"
                        ref={videoInputRef}
                        accept="video/*"
                        multiple
                        onChange={handleVideoUpload}
                        className="d-none"
                      />
                    </div>

                    {renderPendingFiles(videoFiles, "video")}
                    {renderUploadedMedia()}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Uploaded Media Tab */}
              <Tab.Pane eventKey="uploaded">
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FaFolder className="me-3" />
                        All Uploaded Media
                      </h4>
                      <Badge bg="info" pill>
                        Total: {uploadedMedia.length}
                      </Badge>
                    </div>

                    {uploadedMedia.length === 0 ? (
                      <Alert variant="info" className="text-center p-5">
                        <FaFolder size={50} className="mb-3 text-muted" />
                        <h5>No media uploaded yet</h5>
                        <p>
                          Go to the Images or Videos tab to upload media files
                        </p>
                      </Alert>
                    ) : (
                      <div>
                        <div className="mb-4">
                          <Row>
                            {uploadedMedia.map((item, index) => (
                              <Col
                                key={index}
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                className="mb-3"
                              >
                                <MediaCard item={item} index={index} />
                              </Col>
                            ))}
                          </Row>
                        </div>

                        <Alert variant="light" className="mt-4">
                          <h6>
                            <FaLink className="me-2" />
                            API Endpoints
                          </h6>
                          <p className="mb-1">
                            <strong>Upload Images:</strong> POST {Imageurl}
                            /upload/images
                          </p>
                          <p className="mb-1">
                            <strong>Upload Videos:</strong> POST {Imageurl}
                            /upload/videos
                          </p>
                          <p className="mb-0 small text-muted">
                            Supports multiple file uploads with FormData
                          </p>
                        </Alert>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>

      {/* Preview Modal */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {previewItem?.type === "image" ? (
              <FaImage className="me-2" />
            ) : (
              <FaVideo className="me-2" />
            )}
            {previewItem?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewItem && (
            <>
              {previewItem.type === "image" ? (
                <img
                  src={
                    previewItem.path
                      ? getFullUrl(previewItem.path)
                      : previewItem.preview
                  }
                  alt={previewItem.name}
                  className="img-fluid rounded"
                  style={{ maxHeight: "70vh" }}
                />
              ) : (
                <video
                  src={
                    previewItem.path
                      ? getFullUrl(previewItem.path)
                      : previewItem.preview
                  }
                  controls
                  className="w-100 rounded"
                  style={{ maxHeight: "70vh" }}
                />
              )}
              <div className="mt-3 text-start">
                <p>
                  <strong>Name:</strong> {previewItem.name}
                </p>
                <p>
                  <strong>Size:</strong> {formatFileSize(previewItem.size)}
                </p>
                <p>
                  <strong>Type:</strong> {previewItem.type}
                </p>
                {previewItem.path && (
                  <>
                    <p>
                      <strong>Path:</strong> {previewItem.path}
                    </p>
                    <p>
                      <strong>Full URL:</strong> {getFullUrl(previewItem.path)}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {previewItem?.path && (
            <Button
              variant="outline-primary"
              onClick={() => copyToClipboard(previewItem.path)}
            >
              {copiedPath === previewItem.path ? (
                <>
                  <FaCheck className="me-2" />
                  Copied!
                </>
              ) : (
                <>
                  <FaCopy className="me-2" />
                  Copy Path
                </>
              )}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowPreviewModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add CSS for spinner animation */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default UploadMedia;
